import React, { useEffect, useMemo } from "react";
import { makeStyles, useTheme } from "@material-ui/core/styles";
import CircularProgress from "@material-ui/core/CircularProgress";
import { ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip, CartesianGrid, Legend } from "recharts";

import { useTimezone } from "../../../../hooks/useTimezone";
import KpiCard from "../../../Reports/KpiCard";
import ReportTable from "../../../Reports/ReportTable";
import { CHART_COLORS } from "../../../Reports/format";
import ServiceRow, { STATUS_COLORS } from "./ServiceRow";
import RealtimeServiceRow from "./RealtimeServiceRow";

const useStyles = makeStyles((theme) => ({
  grid: { display: "grid", gap: 16 },
  resources: { display: "grid", gap: 16, gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))" },
  row2: { display: "grid", gap: 16, gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))" },
  card: {
    borderRadius: 12,
    border: `1px solid ${theme.palette.divider}`,
    backgroundColor: theme.palette.background.paper,
    padding: "16px 18px",
    minWidth: 0,
  },
  label: { fontSize: 12, fontWeight: 600, letterSpacing: 0.4, textTransform: "uppercase", color: theme.palette.text.secondary },
  value: { fontSize: 30, fontWeight: 700, marginTop: 6, lineHeight: 1.1 },
  detail: { fontSize: 13, color: theme.palette.text.secondary, marginTop: 6 },
  track: { height: 6, borderRadius: 3, backgroundColor: theme.palette.action.hover, marginTop: 12, overflow: "hidden" },
  bar: { height: "100%", borderRadius: 3 },
  cardTitle: { fontSize: 13, fontWeight: 700, letterSpacing: 0.4, textTransform: "uppercase", color: theme.palette.text.primary, marginBottom: 8 },
  cardNote: { fontSize: 12, color: theme.palette.text.secondary, marginTop: 8 },
  infoRow: { display: "flex", justifyContent: "space-between", gap: 12, padding: "10px 0", fontSize: 14, borderBottom: `1px solid ${theme.palette.divider}`, "&:last-child": { borderBottom: "none" } },
  infoKey: { color: theme.palette.text.secondary },
  infoValue: { fontWeight: 600, color: theme.palette.text.primary, textAlign: "right" },
  sectionTitle: { margin: "8px 0 0", fontSize: 16, fontWeight: 700, color: theme.palette.text.primary },
  center: { display: "flex", justifyContent: "center", alignItems: "center", minHeight: 200 },
  empty: { fontSize: 14, color: theme.palette.text.secondary, padding: "24px 0", textAlign: "center" },
}));

// Nombres de las tablas tal como los entiende quien administra, no como se llaman en la base.
const TABLES = [
  ["messages", "Mensajes"],
  ["tickets", "Conversaciones (tickets)"],
  ["contacts", "Contactos"],
  ["campaignShipping", "Envíos de campañas"],
  ["campaigns", "Campañas"],
  ["groupCampaignShippings", "Envíos a grupos"],
  ["groupCampaigns", "Campañas a grupos"],
  ["groupCampaignHourlySchedules", "Horarios de campañas a grupos"],
  ["groupWhatsApp", "Grupos de WhatsApp"],
  ["groupWhatsAppParticipants", "Participantes de grupos"],
  ["quickMessages", "Respuestas rápidas"],
  ["userRatings", "Valoraciones"],
];

const GB = 1024 ** 3;
const num = (v) => Number(v) || 0;
const fmtGb = (bytes) => `${(num(bytes) / GB).toLocaleString(undefined, { maximumFractionDigits: 1 })} GB`;
const fmtInt = (v) => num(v).toLocaleString();
const fmtMb = (mb) => {
  const v = num(mb);
  if (v < 1) return `${Math.round(v * 1024)} KB`;
  if (v < 1024) return `${v.toLocaleString(undefined, { maximumFractionDigits: 1 })} MB`;
  return `${(v / 1024).toLocaleString(undefined, { maximumFractionDigits: 2 })} GB`;
};

/** ok < aviso (limpieza automática) < crítico, con los umbrales configurados. */
const levelFor = (pct, thresholds, fallback = [75, 85]) => {
  const warn = num(thresholds?.autoCleanThreshold) || fallback[0];
  const crit = num(thresholds?.criticalThreshold) || fallback[1];
  if (pct >= crit) return "error";
  if (pct >= warn) return "warning";
  return "ok";
};

const serviceStatus = (s) => (s === "active" ? "ok" : s === "inactive" ? "error" : "unknown");

const ResourceCard = ({ classes, label, value, detail, pct, level }) => (
  <div className={classes.card}>
    <div className={classes.label}>{label}</div>
    <div className={classes.value} style={{ color: STATUS_COLORS[level] }}>{value}</div>
    <div className={classes.detail}>{detail}</div>
    <div className={classes.track}>
      <div className={classes.bar} style={{ width: `${Math.min(100, Math.max(0, pct))}%`, backgroundColor: STATUS_COLORS[level] }} />
    </div>
  </div>
);

/**
 * Resumen del servidor (doc 0447, D6c): recursos, servicios, evolución y base de datos.
 * Sustituye a «Estado del sistema» y a «Gráficos» con el lenguaje visual de Informes.
 */
const Overview = ({ loading, error, systemStatus: st, thresholds, databaseActivity: db, history, onFetchHistory }) => {
  const classes = useStyles();
  const theme = useTheme();
  const { formatDateTime, formatTime } = useTimezone();

  useEffect(() => {
    onFetchHistory(7);
  }, [onFetchHistory]);

  // El historial guarda solo las últimas 20 mediciones (trg_vps_status_after_insert) y llega
  // de la más nueva a la más vieja.
  const series = useMemo(
    () =>
      [...(history || [])].reverse().map((h) => ({
        at: formatTime(h.createdAt),
        disk: num(h.diskUsage),
        memory: num(h.memoryUsage),
        cpu: num(h.cpuUsage),
      })),
    [history, formatTime]
  );

  const tableRows = useMemo(() => {
    if (!db) return [];
    return TABLES.map(([key, name]) => ({
      key,
      name,
      daily: num(db.daily?.[key]),
      total: num(db.historical?.[key]),
      sizeMb: num(db.tableSizes?.[key]),
    }));
  }, [db]);

  if (!st) {
    if (loading) return <div className={classes.center}><CircularProgress /></div>;
    return <div className={classes.empty}>{error ? `No se pudo leer el estado: ${error}` : "Sin datos. Pulsa «Actualizar»."}</div>;
  }

  const disk = num(st.diskUsage);
  const memory = num(st.memoryUsage);
  const cpu = num(st.cpuUsage);
  const binlogGb = num(st.binaryLogsSize);
  const binlogLimit = num(thresholds?.binlogSizeThreshold) || 5;
  const binlogPct = (binlogGb / binlogLimit) * 100;

  const tableColumns = [
    { key: "name", label: "Datos", sort: (r) => r.name },
    { key: "daily", label: "Últimas 24 h", align: "right", render: (r) => fmtInt(r.daily) },
    { key: "total", label: "Total", align: "right", render: (r) => fmtInt(r.total) },
    { key: "sizeMb", label: "Espacio", align: "right", render: (r) => fmtMb(r.sizeMb) },
  ];

  return (
    <div className={classes.grid}>
      <div className={classes.resources}>
        <ResourceCard
          classes={classes}
          label="Disco"
          value={`${disk.toFixed(1)} %`}
          detail={`${fmtGb(st.diskUsed)} de ${fmtGb(st.diskTotal)} · libres ${fmtGb(st.diskAvailable)}`}
          pct={disk}
          level={levelFor(disk, thresholds)}
        />
        <ResourceCard
          classes={classes}
          label="Memoria"
          value={`${memory.toFixed(1)} %`}
          detail={`${fmtGb(st.memoryUsed)} de ${fmtGb(st.memoryTotal)}`}
          pct={memory}
          level={levelFor(memory, thresholds)}
        />
        <ResourceCard
          classes={classes}
          label="CPU"
          value={`${cpu.toFixed(1)} %`}
          detail="Uso en la última medición"
          pct={cpu}
          level={levelFor(cpu, null, [60, 80])}
        />
        <ResourceCard
          classes={classes}
          label="Registros binarios de MySQL"
          value={`${binlogGb.toLocaleString(undefined, { maximumFractionDigits: 2 })} GB`}
          detail={`${fmtInt(st.binaryLogsCount)} archivos · límite ${binlogLimit} GB`}
          pct={binlogPct}
          level={levelFor(binlogPct, null, [80, 100])}
        />
      </div>

      <div className={classes.row2}>
        <div className={classes.card}>
          <div className={classes.cardTitle}>Servicios</div>
          <ServiceRow name="MySQL" status={serviceStatus(st.mysqlStatus)} value={st.mysqlStatus === "active" ? "Activo" : st.mysqlStatus || "Desconocido"} />
          <ServiceRow name="Redis" status={serviceStatus(st.redisStatus)} value={st.redisStatus === "active" ? "Activo" : st.redisStatus || "Desconocido"} />
          {/* -1 = el backend corre en Docker y no usa PM2 (doc 0434, M3) */}
          <ServiceRow
            name="PM2"
            status={num(st.pm2AppsCount) < 0 ? "unknown" : num(st.pm2AppsCount) > 0 ? "ok" : "warning"}
            value={num(st.pm2AppsCount) < 0 ? "No aplica (Docker)" : `${num(st.pm2AppsCount)} procesos en línea`}
          />
          <RealtimeServiceRow />
        </div>

        <div className={classes.card}>
          <div className={classes.cardTitle}>Servidor</div>
          <div className={classes.infoRow}><span className={classes.infoKey}>Nombre</span><span className={classes.infoValue}>{st.serverName || "—"}</span></div>
          <div className={classes.infoRow}><span className={classes.infoKey}>IP</span><span className={classes.infoValue}>{st.serverIP || "—"}</span></div>
          <div className={classes.infoRow}><span className={classes.infoKey}>Última medición</span><span className={classes.infoValue}>{st.updatedAt ? formatDateTime(st.updatedAt) : "—"}</span></div>
        </div>
      </div>

      <div className={classes.card}>
        <div className={classes.cardTitle}>Evolución de recursos</div>
        {series.length < 2 ? (
          <div className={classes.empty}>Todavía no hay suficientes mediciones para dibujar la evolución.</div>
        ) : (
          <ResponsiveContainer width="100%" height={240}>
            <LineChart data={series} margin={{ top: 8, right: 8, left: -16, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke={theme.palette.divider} />
              <XAxis dataKey="at" tick={{ fontSize: 12, fill: theme.palette.text.secondary }} />
              <YAxis domain={[0, 100]} unit="%" tick={{ fontSize: 12, fill: theme.palette.text.secondary }} />
              <Tooltip formatter={(v) => `${Number(v).toFixed(1)} %`} />
              <Legend />
              <Line type="monotone" dataKey="disk" name="Disco" stroke={CHART_COLORS[0]} strokeWidth={2} dot={false} />
              <Line type="monotone" dataKey="memory" name="Memoria" stroke={CHART_COLORS[4]} strokeWidth={2} dot={false} />
              <Line type="monotone" dataKey="cpu" name="CPU" stroke={CHART_COLORS[2]} strokeWidth={2} dot={false} />
            </LineChart>
          </ResponsiveContainer>
        )}
        <div className={classes.cardNote}>Últimas {series.length} mediciones: el servidor conserva solo las 20 más recientes.</div>
      </div>

      <h2 className={classes.sectionTitle}>Base de datos</h2>
      {db ? (
        <>
          <div className={classes.resources}>
            <KpiCard label="Registros nuevos (24 h)" value={fmtInt(tableRows.reduce((s, r) => s + r.daily, 0))} hint="En las tablas de la lista" />
            <KpiCard label="Registros totales" value={fmtInt(tableRows.reduce((s, r) => s + r.total, 0))} />
            <KpiCard label="Espacio ocupado" value={fmtMb(tableRows.reduce((s, r) => s + r.sizeMb, 0))} hint="Datos más índices" />
          </div>
          <ReportTable title="Crecimiento por tipo de dato" columns={tableColumns} rows={tableRows} defaultSort="total" rowKey="key" />
        </>
      ) : (
        <div className={classes.empty}>Sin datos de la base de datos.</div>
      )}
    </div>
  );
};

export default Overview;
